/**
 * カート／レジ／完了の**シート状態**（M1.9b・D-101＝Design 完全忠実化・2026-08-10 森井氏決定④）。
 *
 * ★Design（order-app.js）の流れ＝どの画面からでも右上のカートボタンで**下からシートが出る**：
 *   カート → 「レジに進む」→ 発注内容の確認 → 確定 → 完了。ページ遷移ではなく
 *   **1枚のシートの中身が入れ替わる**（OA.sheet の k: "cart"|"checkout"|"done" と同じ形）。
 * ★旧 `/cart`・`/checkout`・`/done/:id` ルートは廃止（このコンテキストが置き換え）。
 *   LINE 通知のディープリンク（/orders/...）・招待リンク（/invite）はルートのまま＝影響なし。
 * ★カートの中身そのもの（cart.tsx）とは別物＝これは「いまどのシートが開いているか」だけを持つ。
 */

import type { CheckoutResult } from "@kenzai/api-client";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/** 完了シートに出す「送付先・お届け」＝確定時のフォーム値の控え（API は返さない＝D-85）。
 *  複数卸で値が割れたときは null（出さない＝間違った1つを言い切らない）。 */
export type DoneMeta = { shipLabel: string | null; date: string | null };

type Mode =
  | { k: "cart" }
  | { k: "checkout" }
  | { k: "done"; result: CheckoutResult; meta: DoneMeta }
  | null;

type CartUiState = {
  mode: Mode;
  openCart: () => void;
  openCheckout: () => void;
  showDone: (result: CheckoutResult, meta: DoneMeta) => void;
  close: () => void;
};

const Ctx = createContext<CartUiState | null>(null);

export function CartUiProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>(null);
  const openCart = useCallback(() => setMode({ k: "cart" }), []);
  const openCheckout = useCallback(() => setMode({ k: "checkout" }), []);
  const showDone = useCallback(
    (result: CheckoutResult, meta: DoneMeta) => setMode({ k: "done", result, meta }),
    [],
  );
  const close = useCallback(() => setMode(null), []);
  const value = useMemo<CartUiState>(
    () => ({ mode, openCart, openCheckout, showDone, close }),
    [mode, openCart, openCheckout, showDone, close],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCartUi(): CartUiState {
  const v = useContext(Ctx);
  if (!v) throw new Error("CartUiProvider の外で useCartUi が呼ばれた");
  return v;
}
